import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

// ==========================================
// 1. KINETIC TYPOGRAPHY COMPOSITION SHOWCASE
// ==========================================
export const KineticTypographyShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 1. Mask / Clip Reveal (from overflow: hidden)
  const maskProgress = interpolate(frame, [10, 35], [100, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // 2. Blur-In Focus Pull (blur(16px) -> blur(0) + opacity)
  const blurProgress = interpolate(frame, [25, 50], [1, 0], {
    easing: Easing.out(Easing.cubic),
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const blurOpacity = interpolate(frame, [25, 45], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // 3. Word-by-Word Cascade
  const words = ["SATSET", "TRANSAKSI", "0.8 DETIK", "TERBUKTI"];

  // 4. Variable Font Morph / Weight Shift
  const weightScale = spring({
    frame: frame - 40,
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0B0D17",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
        color: "#FFFFFF",
        padding: "100px 60px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Background Accent Glow */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          left: "10%",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(108,92,231,0.25), transparent 70%)",
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "20%",
          right: "10%",
          width: 450,
          height: 450,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.2), transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      {/* Header Badge */}
      <div style={{ zIndex: 10 }}>
        <div
          style={{
            display: "inline-block",
            padding: "8px 20px",
            borderRadius: 999,
            backgroundColor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: "#A78BFA",
            marginBottom: 16,
          }}
        >
          REMOTION SKILL • KINETIC TYPOGRAPHY
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
          4 Master Reveal Techniques
        </h1>
      </div>

      {/* 4 Cards Demonstrating Each Technique */}
      <div style={{ display: "flex", flexDirection: "column", gap: 32, zIndex: 10 }}>
        {/* Style 1: Mask / Clip Wipe */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            padding: "28px 36px",
          }}
        >
          <div style={{ fontSize: 18, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            STYLE 1: MASK / CLIP BASELINE WIPE (Zero Blur, Ultra Sharp)
          </div>
          <div style={{ overflow: "hidden", height: 64 }}>
            <div
              style={{
                fontSize: 48,
                fontWeight: 900,
                color: "#60A5FA",
                transform: `translateY(${maskProgress}%)`,
                letterSpacing: "-0.02em",
              }}
            >
              CLEAN EDITORIAL REVEAL
            </div>
          </div>
        </div>

        {/* Style 2: Blur-In Focus Pull */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            padding: "28px 36px",
          }}
        >
          <div style={{ fontSize: 18, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            STYLE 2: CINEMATIC BLUR-IN FOCUS PULL (Lensa Sinematik)
          </div>
          <div
            style={{
              fontSize: 48,
              fontWeight: 800,
              color: "#34D399",
              filter: `blur(${blurProgress * 16}px)`,
              opacity: blurOpacity,
              letterSpacing: "-0.01em",
            }}
          >
            FOCUS PULL TYPOGRAPHY
          </div>
        </div>

        {/* Style 3: Word-by-Word Cascade */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            padding: "28px 36px",
          }}
        >
          <div style={{ fontSize: 18, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            STYLE 3: WORD-BY-WORD SPRING CASCADE (Staggered Pacing)
          </div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {words.map((w, idx) => {
              const sp = spring({
                frame: frame - 15 - idx * 6,
                fps,
                config: { damping: 14, stiffness: 140 },
              });
              return (
                <span
                  key={idx}
                  style={{
                    fontSize: 40,
                    fontWeight: 800,
                    color: "#FBBF24",
                    display: "inline-block",
                    opacity: Math.max(0, sp),
                    transform: `translateY(${(1 - Math.max(0, sp)) * 30}px) scale(${0.8 + Math.max(0, sp) * 0.2})`,
                  }}
                >
                  {w}
                </span>
              );
            })}
          </div>
        </div>

        {/* Style 4: Variable Font Morph / Scale Punch */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 24,
            padding: "28px 36px",
          }}
        >
          <div style={{ fontSize: 18, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            STYLE 4: PUNCH IMPACT & WEIGHT EMPHASIS
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
            <span style={{ fontSize: 36, color: "#E5E7EB", fontWeight: 400 }}>Harga Mulai</span>
            <span
              style={{
                fontSize: 56,
                fontWeight: 900,
                color: "#F43F5E",
                transform: `scale(${0.9 + weightScale * 0.18})`,
                display: "inline-block",
                textShadow: "0 0 25px rgba(244,63,94,0.4)",
              }}
            >
              RP 27.500!
            </span>
          </div>
        </div>
      </div>

      {/* Footer Standard */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          paddingTop: 24,
          display: "flex",
          justifyContent: "space-between",
          color: "#6B7280",
          fontSize: 18,
          fontWeight: 600,
          zIndex: 10,
        }}
      >
        <span>REMOTION ENGINE v4.0</span>
        <span>TIKTOK SAFE ZONE COMPLIANT</span>
      </div>
    </AbsoluteFill>
  );
};

// ==========================================
// 2. CAPTION & SUBTITLE THEMES SHOWCASE
// ==========================================
export const CaptionThemesShowcase: React.FC = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0D1117",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
        color: "#FFFFFF",
        padding: "90px 60px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header */}
      <div>
        <div
          style={{
            display: "inline-block",
            padding: "8px 20px",
            borderRadius: 999,
            backgroundColor: "rgba(245,158,11,0.15)",
            border: "1px solid rgba(245,158,11,0.3)",
            fontSize: 22,
            fontWeight: 700,
            color: "#F59E0B",
            marginBottom: 16,
          }}
        >
          REMOTION SKILL • CAPTIONS THEMES
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
          Koleksi Style Subtitle Dinamis
        </h1>
      </div>

      {/* 4 Iconic Caption Theme Cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
        {/* 1. BEAST STYLE */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24,
            padding: "24px 32px",
          }}
        >
          <div style={{ fontSize: 16, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            THEME 1: BEAST STYLE (High Energy Viral Pop)
          </div>
          <div
            style={{
              fontSize: 42,
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "-0.01em",
              textAlign: "center",
              padding: "16px 0",
            }}
          >
            <span style={{ color: "#FFFFFF", textShadow: "0 4px 12px rgba(0,0,0,0.8)" }}>PROSES HANYA </span>
            <span
              style={{
                color: "#FACC15",
                backgroundColor: "#000000",
                padding: "4px 16px",
                borderRadius: 12,
                border: "3px solid #FACC15",
                display: "inline-block",
                transform: "scale(1.1) rotate(-1deg)",
                boxShadow: "0 8px 24px rgba(250,204,21,0.3)",
              }}
            >
              0.8 DETIK
            </span>
            <span style={{ color: "#FFFFFF", textShadow: "0 4px 12px rgba(0,0,0,0.8)" }}> SATSET!</span>
          </div>
        </div>

        {/* 2. KARAOKE STYLE */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24,
            padding: "24px 32px",
          }}
        >
          <div style={{ fontSize: 16, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            THEME 2: KARAOKE WORD-FILL (Sinkronisasi Audio Presisi)
          </div>
          <div
            style={{
              fontSize: 38,
              fontWeight: 800,
              textAlign: "center",
              padding: "16px 0",
              display: "flex",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <span style={{ color: "#10B981" }}>Top-up</span>
            <span style={{ color: "#10B981" }}>resmi</span>
            <span style={{ color: "#10B981" }}>paling</span>
            <span style={{ color: "#10B981", textDecoration: "underline" }}>murah</span>
            <span style={{ color: "#4B5563" }}>dan</span>
            <span style={{ color: "#4B5563" }}>terpercaya</span>
          </div>
        </div>

        {/* 3. GAMING STREAM HUD STYLE */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24,
            padding: "24px 32px",
          }}
        >
          <div style={{ fontSize: 16, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            THEME 3: GAMING STREAM HUD (Esports & Live Streamer Vibe)
          </div>
          <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
            <div
              style={{
                backgroundColor: "rgba(17,24,39,0.9)",
                border: "2px solid #3B82F6",
                borderRadius: 16,
                padding: "14px 28px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                boxShadow: "0 0 25px rgba(59,130,246,0.3)",
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#EF4444",
                  boxShadow: "0 0 8px #EF4444",
                }}
              />
              <span style={{ fontSize: 32, fontWeight: 800, color: "#F3F4F6", letterSpacing: "0.02em" }}>
                DIAMOND LANGSUNG MASUK KE INBOX
              </span>
            </div>
          </div>
        </div>

        {/* 4. HUSTLE DYNAMIC BOX */}
        <div
          style={{
            backgroundColor: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24,
            padding: "24px 32px",
          }}
        >
          <div style={{ fontSize: 16, color: "#9CA3AF", marginBottom: 12, fontWeight: 600 }}>
            THEME 4: HUSTLE SKEWED (Bold Streetwear / Tik-Tok Fast-Paced)
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, padding: "12px 0" }}>
            <div
              style={{
                backgroundColor: "#DC2626",
                color: "#FFFFFF",
                fontSize: 34,
                fontWeight: 900,
                fontStyle: "italic",
                padding: "8px 20px",
                transform: "skewX(-10deg)",
                boxShadow: "0 6px 18px rgba(220,38,38,0.4)",
              }}
            >
              GARANSI 100%
            </div>
            <div
              style={{
                backgroundColor: "#FFFFFF",
                color: "#000000",
                fontSize: 34,
                fontWeight: 900,
                fontStyle: "italic",
                padding: "8px 20px",
                transform: "skewX(-10deg)",
              }}
            >
              ANTI-MINUS RESMI
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          paddingTop: 24,
          display: "flex",
          justifyContent: "space-between",
          color: "#6B7280",
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        <span>13 DYNAMIC CAPTIONS PRESETS</span>
        <span>Y: 1500px SAFE ZONE ALIGNED</span>
      </div>
    </AbsoluteFill>
  );
};
